'use client'
import { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import LinkComponent from './LinkComponent'
import { sendGAEvent } from '@next/third-parties/google'

// ─── Icons ────────────────────────────────────────────────────────────────────

const iconMap = {
  calendar: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  clock: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const rc = (val, fb = '#374151') => val || fb

function formatDate(iso, fmt) {
  if (!iso) return ''
  const d = new Date(iso)
  if (fmt === 'datetime') return d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
  if (fmt === 'relative') {
    const diff = Math.floor((Date.now() - d) / 86400000)
    if (diff === 0) return 'Today'
    if (diff === 1) return 'Yesterday'
    if (diff < 30)  return `${diff} days ago`
    if (diff < 365) return `${Math.floor(diff / 30)} months ago`
    return `${Math.floor(diff / 365)} years ago`
  }
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })
}

function estimateReadTime(content, wpm = 200) {
  const words = (content ?? '').trim().split(/\s+/).length
  const mins  = Math.ceil(words / wpm)
  return `${mins} min read`
}

function posStyle(pos) {
  const base = { position: 'absolute', zIndex: 2, display: 'flex', flexDirection: 'column', gap: 4 }
  if (pos === 'top-left')     return { ...base, top: 8, left: 8 }
  if (pos === 'top-right')    return { ...base, top: 8, right: 8, alignItems: 'flex-end' }
  if (pos === 'bottom-left')  return { ...base, bottom: 8, left: 8 }
  if (pos === 'bottom-right') return { ...base, bottom: 8, right: 8, alignItems: 'flex-end' }
  return { ...base, top: 8, left: 8 }
}

function badgeStyle(field, onImage = false) {
  const v     = field.variant ?? 'filled'
  const color = rc(field.color, '#228be6')
  const tc    = rc(field.textColor, '#ffffff')
  if (v === 'filled')   return { background: onImage ? `${color}dd` : color, color: tc,    border: 'none' }
  if (v === 'outlined') return { background: 'transparent', color: onImage ? '#fff' : color, border: `1.5px solid ${onImage ? 'rgba(255,255,255,0.8)' : color}` }
  return                       { background: `${color}20`,  color,           border: `1px solid ${color}35` }
}

// ─── BlogCard ─────────────────────────────────────────────────────────────────

const BlogCard = ({ item, cardStyle, newLimit, isLast, dictionary, lang }) => {

  const cardRef = useRef()

  // intersection observer for infinite scroll
  useEffect(() => {
    if (!cardRef?.current || !isLast || !newLimit) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { newLimit(); observer.unobserve(entry.target) }
    })
    observer.observe(cardRef.current)
    return () => observer.disconnect()
  }, [isLast, newLimit])

  // ── Config with fallbacks ──────────────────────────────────────────────
  const cfg    = cardStyle ?? {}
  const card   = cfg.card   ?? {}
  const fields = cfg.fields ?? []

  const cardBg         = card.bg                  ?? '#ffffff'
  const cardRadius     = card.radius              ?? 12
  const borderWidth    = card.borderWidth         ?? 0
  const borderColor    = card.borderColor         ?? '#e5e7eb'
  const cardShadow     = card.shadow              ?? '0 4px 24px rgba(0,0,0,0.10)'
  const imageRatio     = card.imageRatio          ?? '16/9'
  const imageRadius    = card.imageRadius         ?? 8
  const contentPadding = card.contentPadding      ?? 14
  const isHorizontal   = card.layout             === 'horizontal'
  const imgWidth       = card.horizontalImageWidth ?? 120

  const getField  = (id) => fields.find(f => f.id === id)

  const categoryField = getField('category')
  const languageField = getField('language')

  // ── Image block ────────────────────────────────────────────────────────
  const imgBlock = (
    <div style={{
      position: 'relative',
      flexShrink: 0,
      ...(isHorizontal
        ? { width: imgWidth, alignSelf: 'stretch' }
        : { width: '100%', aspectRatio: imageRatio }),
      borderRadius: imageRadius,
      overflow: 'hidden',
    }}>
      <Image
        src={item?.cloudThumbnail || '/images/test.jpg'}
        fill
        style={{ objectFit: 'cover' }}
        alt={item?.slug ?? 'blog'}
      />

      {/* Category badge on image */}
      {categoryField?.enabled && categoryField?.showOnImage && (
        <div style={posStyle(categoryField.position ?? 'top-left')}>
          <span style={{
            ...badgeStyle(categoryField, true),
            display: 'inline-flex', alignItems: 'center',
            fontSize: `${categoryField.fontSize ?? 0.7}rem`,
            fontWeight: categoryField.fontWeight ?? '600',
            padding: '2px 8px',
            borderRadius: categoryField.radius ?? 4,
            backdropFilter: 'blur(4px)',
            lineHeight: 1.4,
          }}>
            {item?.category ?? (dictionary?.blog?.category ?? 'Blog')}
          </span>
        </div>
      )}

      {/* Language badge on image */}
      {languageField?.enabled && languageField?.showOnImage && item?.language && (
        <div style={posStyle(languageField.position ?? 'top-right')}>
          <span style={{
            ...badgeStyle(languageField, true),
            display: 'inline-flex', alignItems: 'center',
            fontSize: `${languageField.fontSize ?? 0.65}rem`,
            fontWeight: languageField.fontWeight ?? '600',
            padding: '2px 8px',
            borderRadius: languageField.radius ?? 4,
            backdropFilter: 'blur(4px)',
            lineHeight: 1.4,
          }}>
            {item.language.toUpperCase()}
          </span>
        </div>
      )}
    </div>
  )

  // ── Content block ──────────────────────────────────────────────────────
  const contentBlock = (
    <div style={{ padding: contentPadding, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {fields
        .filter(f => f.enabled || f.isDivider)
        .map(field => {

          // Divider
          if (field.isDivider) return (
            <div key={field.id} style={{ marginTop: field.marginTop ?? 4, marginBottom: field.marginBottom ?? 4, borderTop: `${field.thickness ?? 1}px ${field.style ?? 'solid'} ${rc(field.color, '#e5e7eb')}` }} />
          )

          // Category badge in content
          if (field.id === 'category' && !field.showOnImage) {
            const bs = badgeStyle(field, false)
            return (
              <span key="cat" style={{
                ...bs,
                display: 'inline-flex', alignSelf: field.align === 'center' ? 'center' : field.align === 'right' ? 'flex-end' : 'flex-start',
                fontSize: `${field.fontSize ?? 0.7}rem`, fontWeight: field.fontWeight ?? '600',
                padding: '2px 8px', borderRadius: field.radius ?? 4, lineHeight: 1.4, marginBottom: 2,
              }}>
                {item?.category ?? (dictionary?.blog?.category ?? 'Blog')}
              </span>
            )
          }

          // Language badge in content
          if (field.id === 'language' && !field.showOnImage && item?.language) {
            const bs = badgeStyle(field, false)
            return (
              <span key="lang" style={{
                ...bs,
                display: 'inline-flex', alignSelf: field.align === 'center' ? 'center' : field.align === 'right' ? 'flex-end' : 'flex-start',
                fontSize: `${field.fontSize ?? 0.65}rem`, fontWeight: field.fontWeight ?? '600',
                padding: '2px 8px', borderRadius: field.radius ?? 4, lineHeight: 1.4, marginBottom: 2,
              }}>
                {item.language.toUpperCase()}
              </span>
            )
          }

          // Title
          if (field.id === 'title') {
            const clamp  = field.lineClamp ?? 2
            const lineH  = 1.3
            const height = field.fixedHeight !== false ? `${clamp * lineH * (field.fontSize ?? 1)}rem` : undefined
            return (
              <div key="title" style={{
                textAlign: field.align,
                height, overflow: 'hidden',
                background: field.bg && field.bg !== 'transparent' ? rc(field.bg) : undefined,
                padding: field.bg && field.bg !== 'transparent' ? '2px 6px' : 0,
                borderRadius: field.bg && field.bg !== 'transparent' ? 4 : 0,
              }}>
                <span style={{ display: '-webkit-box', WebkitLineClamp: clamp, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: `${field.fontSize ?? 1}rem`, fontWeight: field.fontWeight ?? '700', color: rc(field.color, '#111827'), lineHeight: lineH }}>
                  {item?.title}
                </span>
              </div>
            )
          }

          // Excerpt
          if (field.id === 'excerpt') {
            if (!item?.excerpt) return null
            const clamp  = field.lineClamp ?? 3
            const lineH  = 1.5
            const height = field.fixedHeight !== false ? `${clamp * lineH * (field.fontSize ?? 0.875)}rem` : undefined
            return (
              <div key="excerpt" style={{ textAlign: field.align, height, overflow: 'hidden' }}>
                <span style={{ display: '-webkit-box', WebkitLineClamp: clamp, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontSize: `${field.fontSize ?? 0.875}rem`, fontWeight: field.fontWeight ?? '400', color: rc(field.color, '#6b7280'), lineHeight: lineH }}>
                  {item.excerpt}
                </span>
              </div>
            )
          }

          // Date
          if (field.id === 'date') {
            if (!item?.createdAt) return null
            return (
              <div key="date" style={{ textAlign: field.align, display: 'flex', alignItems: 'center', justifyContent: field.align === 'center' ? 'center' : field.align === 'right' ? 'flex-end' : 'flex-start', gap: 5 }}>
                {field.icon && field.icon !== 'none' && iconMap[field.icon] && (
                  <span style={{ color: rc(field.color), opacity: 0.7, display: 'flex', flexShrink: 0 }}>{iconMap[field.icon]}</span>
                )}
                <span style={{ fontSize: `${field.fontSize ?? 0.8125}rem`, fontWeight: field.fontWeight ?? '400', color: rc(field.color, '#6b7280') }}>
                  {formatDate(item.createdAt, field.format ?? 'date')}
                </span>
              </div>
            )
          }

          // Read time
          if (field.id === 'readtime') {
            const text = estimateReadTime(item?.content, field.wordsPerMinute ?? 200)
            return (
              <div key="readtime" style={{ textAlign: field.align, display: 'flex', alignItems: 'center', justifyContent: field.align === 'center' ? 'center' : field.align === 'right' ? 'flex-end' : 'flex-start', gap: 5 }}>
                {field.icon && field.icon !== 'none' && iconMap[field.icon] && (
                  <span style={{ color: rc(field.color), opacity: 0.7, display: 'flex', flexShrink: 0 }}>{iconMap[field.icon]}</span>
                )}
                <span style={{ fontSize: `${field.fontSize ?? 0.8125}rem`, fontWeight: field.fontWeight ?? '400', color: rc(field.color, '#6b7280') }}>
                  {text}
                </span>
              </div>
            )
          }

          return null
        })}
    </div>
  )

  return (
    <div
      ref={cardRef}
      style={{
        background: rc(cardBg, '#fff'),
        borderRadius: cardRadius,
        border: borderWidth > 0 ? `${borderWidth}px solid ${rc(borderColor)}` : 'none',
        boxShadow: cardShadow,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: isHorizontal ? 'row' : 'column',
        width: '100%',
        cursor: 'pointer',
      }}
      onClick={() => sendGAEvent(
        'event',
        'select_content',
        { 
          blog_id: item?._id?.toString(), 
          blog_name: item?.title 
        })}
    >
      <LinkComponent href={`/blogs/${item?.slug}`} style={{ display: 'contents' }}>
        {imgBlock}
        {contentBlock}
      </LinkComponent>
    </div>
  )
}

export default BlogCard